import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { FaBolt } from "react-icons/fa";
import { getPublishedBlogs } from "../../services/blogService";
import "./NewsPage.css";

const formatDate = (value) => {
	if (!value) {
		return "";
	}
	return new Date(value).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
};

const getCoverPosition = (blog) => {
	const x =
		typeof blog?.coverImage?.focalX === "number" ? blog.coverImage.focalX : 50;
	const y =
		typeof blog?.coverImage?.focalY === "number" ? blog.coverImage.focalY : 50;
	return `${x}% ${y}%`;
};

const NewsPage = () => {
	const [blogs, setBlogs] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchBlogs = async () => {
			try {
				setLoading(true);
				const response = await getPublishedBlogs({ limit: 50 });
				setBlogs(response?.data?.items || []);
			} catch (error) {
				console.error("Failed to load news:", error);
				setBlogs([]);
			} finally {
				setLoading(false);
			}
		};
		void fetchBlogs();
	}, []);

	const featured = useMemo(() => blogs[0] || null, [blogs]);
	const remaining = useMemo(() => blogs.slice(1), [blogs]);

	return (
		<Container className="news-page">
			<Helmet>
				<title>News - The Race Companion</title>
				<meta
					name="description"
					content="Latest mountain bike news, insights, and stories from The Race Companion."
				/>
			</Helmet>

			<h1 className="news-heading">News</h1>
			<p className="news-subheading">
				Latest stories and updates from The Race Companion.
			</p>

			{loading ? (
				<div className="news-loading">Loading latest news...</div>
			) : !featured ? (
				<div className="news-empty">No news articles published yet.</div>
			) : (
				<>
					<Link className="news-card-link" to={`/news/${featured.slug}`}>
						<Card className="news-featured-card">
							<div className="news-featured-image-wrap">
								{featured?.coverImage?.url ? (
									<img
										src={featured.coverImage.url}
										alt={featured.title}
										className="news-featured-image"
										style={{ objectPosition: getCoverPosition(featured) }}
									/>
								) : null}
								<div className="news-featured-badge">
									<FaBolt className="news-featured-badge__icon" />
									<span>LATEST</span>
								</div>
							</div>
							<Card.Body className="news-featured-body">
								<div className="news-date">{formatDate(featured.publishedAt)}</div>
								<Card.Title className="news-featured-title">
									{featured.title}
								</Card.Title>
								<Card.Text className="news-featured-excerpt">
									{featured.excerpt || "Read the full story."}
								</Card.Text>
							</Card.Body>
						</Card>
					</Link>

					<Row className="g-3 news-grid">
						{remaining.map((blog) => (
							<Col key={blog._id} md={6} lg={4}>
								<Link className="news-card-link" to={`/news/${blog.slug}`}>
									<Card className="news-small-card">
										<div className="news-small-image-wrap">
											{blog?.coverImage?.url ? (
												<img
													src={blog.coverImage.url}
													alt={blog.title}
													className="news-small-image"
													style={{ objectPosition: getCoverPosition(blog) }}
												/>
											) : null}
										</div>
										<Card.Body className="news-small-body">
											<div className="news-date">{formatDate(blog.publishedAt)}</div>
											<Card.Title className="news-small-title">
												{blog.title}
											</Card.Title>
											<Card.Text className="news-small-excerpt">
												{blog.excerpt || "Read more"}
											</Card.Text>
										</Card.Body>
									</Card>
								</Link>
							</Col>
						))}
					</Row>
				</>
			)}
		</Container>
	);
};

export default NewsPage;
